"use client"

import type React from "react"
import { useEffect, useState } from "react"
import { MdEdit, MdDelete } from "react-icons/md"
import { useDraftManager } from "../../../hooks/useDraftManager"
import { Spinner } from "react-bootstrap"
import Swal from "sweetalert2"
import "../../../styles/draftTable.css"
import { useParams } from "react-router-dom"
import { ToastContainer } from "react-toastify"
import { Alert } from "antd"
import { ExclamationCircleFilled } from "@ant-design/icons"
import { trackMenuClick } from "../../../services/menuClick_service";

// Définition explicite du type DraftItem pour correspondre aux données renvoyées par l'API
interface DraftItem {
  id: string
  announcementType: string
  createdAt: string
  updatedAt: string
}

const DraftTable: React.FC = () => {
  const [drafts, setDrafts] = useState<DraftItem[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const userStatus = localStorage.getItem("userStatus") || "anonymous";

  const { fetchDrafts, deleteDraft, loadDraft } = useDraftManager()

  const { draftId } = useParams()
  useEffect( () => {
    if( Number.isInteger(draftId) ){
      console.log("mode edition")
    }
    else{
      console.log("listage")
    }
  }, [])

  useEffect(() => {
    loadDrafts()
  }, [])

  const loadDrafts = async () => {
    setLoading(true)
    try {
      const draftsList = await fetchDrafts()
      // Assurez-vous que les données reçues correspondent au type DraftItem
      const typedDrafts = draftsList.map((draft) => ({
        id: draft.id || "",
        announcementType: draft.announcementType || draft.announcement_type || "Type non spécifié",
        createdAt: draft.createdAt || new Date().toISOString(),
        updatedAt: draft.updatedAt || new Date().toISOString(),
      })) as DraftItem[]

      setDrafts(typedDrafts)
    } catch (error) {
      console.error("Erreur lors du chargement des brouillons:", error)
    } finally {
      setLoading(false)
    }
  }

  const confirmDelete = (id: string) => {
    trackMenuClick('draft-supprimer', `Supprimer brouillon ID : ${id}`);
    Swal.fire({
      title: "Confirmation de suppression",
      text: "Êtes-vous sûr de vouloir supprimer ce brouillon ? Cette action est irréversible.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E76F51",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Supprimer",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const success = await deleteDraft(id)
        if (success) {
          setDrafts(drafts.filter((draft) => draft.id !== id))
          Swal.fire("Supprimé !", "Le brouillon a été supprimé avec succès.", "success")
        } else {
          Swal.fire("Erreur", "Une erreur est survenue lors de la suppression.", "error")
        }
      }
    })
  }

  const handleEdit = async (draft: DraftItem) => {
    trackMenuClick('draft-modifier', `Modifier brouillon : ${draft.announcementType}`);
    try {
      // Charger directement le brouillon par son ID
      const success = await loadDraft(draft.id)
      if (!success) {
        Swal.fire("Erreur", "Impossible de charger ce brouillon.", "error")
      }
      // La navigation est gérée dans loadDraft
    } catch (error) {
      console.error("Erreur lors de l'édition du brouillon:", error)
      Swal.fire("Erreur", "Une erreur est survenue lors du chargement du brouillon.", "error")
    }
  }

  if (loading) {
    return (
      <div className="draft-loading-container">
        <Spinner animation="border" variant="primary" />
        <p>Chargement des brouillons...</p>
      </div>
    )
  }

  if (userStatus === "anonymous") {
          return (
              <>
                  <ToastContainer />
                  <div className="gp-root container-fluid">
                      <div className="mt-5 text-center">
                          <Alert
                              className="custom-alert-white"
                              message="Module de brouillon indisponible"
                              description="Ravi de vous compter parmi nous ! Publiez votre première annonce et finalisez votre inscription pour profiter de ce module."
                              type="warning"
                              showIcon
                              icon={<ExclamationCircleFilled style={{ color: '#eb8635' }} />}
                              style={{
                                  fontSize: '1.1rem',
                              }}
                          />
                      </div>
                  </div>
              </>
          );
      }
  

  return (
    <div className="draft-table-container">
      <h3 className="draft-table-title">Mes brouillons</h3>

      {drafts.length === 0 ? (
        <div className="draft-empty-state">
          <p>Vous n'avez pas encore de brouillons sauvegardés.</p>
        </div>
      ) : (
        <table className="draft-custom-table">
          <thead>
            <tr>
              <th className="col-num">Num</th>
              <th className="col-annonce">Type Annonce</th>
              <th className="col-date">Date création</th>
              <th className="col-modifier">Modifier</th>
              <th className="col-supprimer">Supprimer</th>
            </tr>
          </thead>
          <tbody>
            {drafts.map((item, index) => (
              <tr key={item.id}>
                <td>{ item.id }</td>
                <td>{item.announcementType}</td>
                <td>{new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" }).format(new Date(item.createdAt))}</td>
                <td className="text-center">
                  <button className="draft-icon-button" aria-label="Modifier" onClick={() => handleEdit(item)}>
                    <MdEdit size={20} color="#2A9D8F" />
                  </button>
                </td>
                <td className="text-center">
                  <button className="draft-icon-button" aria-label="Supprimer" onClick={() => confirmDelete(item.id)}>
                    <MdDelete size={20} color="#E76F51" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

export default DraftTable
